<template>
    <div class="print-risk-assessment">
        <e-progress-page v-if="isLoading" class="print-risk-assessment__progress-page"/>
        <div v-else>
            <v-layout class="print-heading">
                <h1>{{ $t('pages.print.risk-assessment.title') }}</h1>
                <e-status-info v-if="showStatusInfo" class="print-risk-assessment__draft-label" id="draft-label">
                    {{ $t('pages.print.risk-assessment.status-info.draft') }}
                </e-status-info>
                <qrcode-vue v-if="showQrCode" :value="url" :size="size" level="H" class="print-qr"></qrcode-vue>
            </v-layout>
            <v-layout class="print-metadata">
                <v-flex>
                    <p v-if="organization">
                        <b>{{$t('pages.print.risk-assessment.company')}}:</b><br>
                        {{organization.name}}<br>
                        {{organization.address}}<br>
                        {{organization.postCode}} {{organization.locality}}
                    </p>
                </v-flex>
                <v-flex>
                    <p v-if="assessment.metadata">
                        <b>{{$t('pages.print.risk-assessment.object')}}:</b><br>
                        {{ assessment.metadata.name }}<br>
                        {{ assessmentObject.address }}<br>
                        {{ assessmentObject.postcode }} {{ assessmentObject.city }}
                    </p>
                </v-flex>
            </v-layout>
            <v-layout class="print-objectdata">
                <v-flex>
                    <p v-if="businessTypes">
                        <b><span>{{$t('pages.print.risk-assessment.businessTypes')}}: </span></b><br>
                        <span class="business-type" v-for="item in businessTypes" :key="item._id">
                          {{ item.title }}<span class="comma">, </span>
                      </span>
                    </p>
                </v-flex>
                <v-flex>
                    <p v-if="owner">
                        <b>{{$t('pages.print.risk-assessment.owner')}}:</b><br>
                        {{owner.fullName}}
                    </p>
                </v-flex>
            </v-layout>
            <p>{{ assessment, assessmentObject | riskAssessmentPrintStatus }}</p>
            <print-config-area
                    :answers="assessment.configWithAnswer"
                    :configuration="assessment.config"
            />
        </div>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import links from "@mixins/helpers/_links";
    import eStatusInfo from "@components/elements/print/e-status-info";
    import EButtonPrimary from "@elements/buttons/theme/e-button-primary";
    import printConfigArea from "@pages/print/concrete/print-config-area";
    import QrcodeVue from 'qrcode.vue';
    import filters from "@mixins/includes/_filters";
    import errorHandler from "@mixins/responses/_errorHandler";
    import notifications from "@mixins/includes/_notifications";

    export default {
        name: "print-risk-assessment",
        components: {
            EButtonPrimary,
            eStatusInfo,
            printConfigArea,
            QrcodeVue
        },
        data() {
            return {
                isLoading: true,
                size: 100,
            }
        },
        mixins: [links, filters, errorHandler, notifications],
        computed: {
            ...mapGetters({
                getAssessment: 'riskAssessments/read',
                getObject: 'objects/read',
                getBusinessTypes: 'objects/business_types',
                getOrganization: 'organizations/read',
                getUser: 'users/read'
            }),
            assessment() {
                return this.getAssessment(this.$route.params.id);
            },
            assessmentObject() {
                return this.objectId ? this.getObject(this.objectId) : null
            },
            objectId() {
                if (this.$lodash.isEmpty(this.assessment)) return null;
                return this.assessment._jv.relationships.objects.data.id.split('/')[2]
            },
            businessTypes() {
                return this.getBusinessTypes
            },
            organizationId() {
                return this.$user.relationships.organization.data.id;
            },
            organization() {
                return this.getOrganization(this.organizationId)
            },
            showStatusInfo() {
                return this.assessment.status !== 'completed'
            },
            showQrCode() {
              return this.assessment.status === 'completed'
            },
            owner() {
                return this.getUser(this.ownerId)
            },
            ownerId() {
                return this.assessmentObject._jv.relationships.owner.data.id
            },
            url() {
                return window.location.origin + '/qr/' + this.assessment._id;
            }
        },
        methods: {
            ...mapActions({
                loadAssessment: 'riskAssessments/read',
                loadObject: 'objects/read',
                loadObjectType: 'objectTypes/read',
                loadBusinessTypes: 'objects/business_types',
                loadOrganization: 'organizations/read',
                loadUser: 'users/read'
            }),
            async requestAssessment() {
                await this.loadAssessment({id: this.$route.params.id})
            },
            async requestObject() {
                await this.loadObject({id: this.objectId})
            },
            async requestBusinessTypes() {
                await this.loadBusinessTypes({id: this.objectId})
            },
            async requestOrganization() {
                await this.loadOrganization({id: this.organizationId})
            },
            async requestUser() {
                await this.loadUser({id: this.ownerId})
            }
        },
        async created() {
            try {
                await this.requestAssessment();
                await this.requestObject();
                await this.requestBusinessTypes();
                await this.requestOrganization();
                await this.requestUser();
                this.isLoading = false
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.notify.error(this.$t('notifications.errors.loseRightsOnRa'))
                    this.redirect(this.nav.dashboard.service.risk_assessment.index.name);
                } else {
                    this.error.handle(e);
                }
            }
        },
    }
</script>

<style lang="scss">
    .print-risk-assessment {
        &__progress-page {
            padding-top: 4rem;
        }

        .print-metadata,
        .print-objectdata {
            width: 80%;

            & > .flex {
                width: 50%;
            }
        }

        .print-qr {
            margin-left: auto;
            height: 0;
        }

        .business-type {
            &:last-of-type {
                .comma {
                    display: none
                }
            }
        }
        &__draft-label {
            margin-bottom: 2rem;
            margin-left: 2rem;
        }
    }
</style>